import { useEffect, useId, useRef, useState } from "react";

const BUTTON_VARIANTS = {
  primary: {
    border: "none",
    background: "#0f766e",
    color: "#fff"
  },
  secondary: {
    border: "1px solid #0f766e",
    background: "#fff",
    color: "#0f766e"
  },
  neutral: {
    border: "1px solid #cbd5e1",
    background: "#f8fafc",
    color: "#0f172a"
  }
};

function ActionMenuButton({
  label,
  items,
  buttonVariant = "secondary",
  align = "left",
  menuMinWidth = 180
}) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const menuId = useId();

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const variantStyle = BUTTON_VARIANTS[buttonVariant] || BUTTON_VARIANTS.secondary;
  const menuPositionStyle = align === "right" ? { right: 0 } : { left: 0 };

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen ? "true" : "false"}
        aria-controls={menuId}
        onClick={() => setIsOpen((current) => !current)}
        className={`collection-button${buttonVariant === "primary" ? " collection-button--primary" : ""}`}
        style={{
          fontSize: "12px",
          borderRadius: "12px",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          ...variantStyle
        }}
      >
        <span>{label}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path
            d="M2.5 4.5L6 8L9.5 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {isOpen ? (
        <div
          id={menuId}
          role="menu"
          className="action-menu-button__menu"
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            zIndex: 30,
            minWidth: `${menuMinWidth}px`,
            padding: "8px",
            borderRadius: "16px",
            border: "1px solid #dbe4f0",
            background: "rgba(255, 255, 255, 0.98)",
            boxShadow: "0 20px 40px rgba(15, 23, 42, 0.14)",
            backdropFilter: "blur(12px)",
            display: "grid",
            gap: "6px",
            ...menuPositionStyle
          }}
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={Boolean(item.disabled)}
              className={`action-menu-button__item${item.tone === "danger" ? " action-menu-button__item--danger" : ""}`}
              onClick={() => {
                if (item.disabled) {
                  return;
                }
                setIsOpen(false);
                item.onClick();
              }}
              style={{
                fontSize: "12px",
                fontWeight: 600,
                cursor: item.disabled ? "not-allowed" : "pointer"
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default ActionMenuButton;